import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { useNotifications } from '../contexts/NotificationsContext';
import { palette, typography, theme } from '../theme/colors';
import { ColorModeContext } from '../contexts/ColorModeContext';

export const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, clearAll } = useNotifications();
  const { mode } = useContext(ColorModeContext);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const colors = mode === 'dark' ? {
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification: any) => {
    markAsRead(notification.id);
    handleClose();

    // Navigate based on notification type
    switch (notification.type) {
      case 'NEW_RESERVATION':
        if (notification.data?.id) {
          navigate(`/app/reservations/${notification.data.id}`);
        } else {
          navigate('/app/reservations?status=pending');
        }
        break;
      case 'NEW_SALE':
        if (notification.data?.id) {
          navigate(`/app/sales/${notification.data.id}`);
        } else {
          navigate('/app/sales');
        }
        break;
      case 'LOW_STOCK':
        navigate('/app/inventory');
        break;
      case 'NEW_B2B_ACCESS_REQUEST':
        navigate('/app/access-requests');
        break;
      default:
        break;
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'NEW_SALE':
        return <ReceiptLongOutlinedIcon sx={{ color: palette.primary, fontSize: 20 }} />;
      case 'LOW_STOCK':
        return <WarningAmberOutlinedIcon sx={{ color: palette.error, fontSize: 20 }} />;
      case 'NEW_RESERVATION':
        return <ShoppingCartOutlinedIcon sx={{ color: palette.warning, fontSize: 20 }} />;
      default:
        return <NotificationsOutlinedIcon sx={{ color: colors.textMuted, fontSize: 20 }} />;
    }
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'NEW_SALE':
        return palette.primary;
      case 'LOW_STOCK':
        return palette.error;
      case 'NEW_RESERVATION':
        return palette.warning;
      default:
        return colors.textMuted;
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (minutes < 1) return 'JUST NOW';
    if (minutes < 60) return `${minutes}M AGO`;
    if (hours < 24) return `${hours}H AGO`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          width: 40,
          height: 40,
          border: `1px solid ${colors.border}`,
          borderRadius: theme.radius.xs,
          backgroundColor: colors.surface,
          color: colors.textSecondary,
          '&:hover': {
            backgroundColor: alpha(palette.primary, 0.1),
            borderColor: palette.primary,
            color: palette.primary,
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: palette.error,
              color: '#FFFFFF',
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              minWidth: 18,
              height: 18,
              borderRadius: theme.radius.xs,
              border: `2px solid ${colors.surface}`,
            },
          }}
        >
          <NotificationsOutlinedIcon sx={{ fontSize: 20 }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 420,
            maxHeight: 600,
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            mt: 1,
          },
        }}
      >
        {/* Industrial Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: colors.surfaceElevated,
            borderBottom: `2px solid ${colors.border}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsOutlinedIcon sx={{ color: palette.primary, fontSize: 20 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.md,
                letterSpacing: typography.letterSpacing.wide,
              }}
            >
              NOTIFICATIONS
            </Typography>
          </Box>
          {notifications.length > 0 && (
            <Typography
              variant="caption"
              onClick={clearAll}
              sx={{
                color: palette.error,
                cursor: 'pointer',
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                border: `1px solid ${colors.border}`,
                px: 1.5,
                py: 0.5,
                borderRadius: theme.radius.xs,
                '&:hover': {
                  backgroundColor: alpha(palette.error, 0.1),
                  borderColor: palette.error,
                },
              }}
            >
              CLEAR ALL
            </Typography>
          )}
        </Box>

        {notifications.length === 0 ? (
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
              backgroundColor: colors.surfaceElevated,
              border: `1px solid ${colors.border}`,
              borderRadius: theme.radius.xs,
              m: 2,
            }}
          >
            <NotificationsOutlinedIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 2 }} />
            <Typography
              variant="body2"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                textTransform: 'uppercase',
                fontWeight: typography.fontWeight.bold,
              }}
            >
              NO NOTIFICATIONS
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 480, overflowY: 'auto' }}>
            {notifications.map((notification) => {
              const typeColor = getTypeColor(notification.type);
              return (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 2,
                    px: 2,
                    m: 1,
                    borderLeft: `4px solid ${typeColor}`,
                    backgroundColor: colors.surfaceElevated,
                    border: `1px solid ${colors.border}`,
                    '&:hover': {
                      backgroundColor: alpha(typeColor, 0.05),
                      borderColor: typeColor,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        backgroundColor: alpha(typeColor, 0.1),
                        border: `1px solid ${typeColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {getIcon(notification.type)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: typography.fontWeight.bold,
                          color: colors.textPrimary,
                          mb: 0.5,
                          fontFamily: typography.fontFamily.display,
                          textTransform: 'uppercase',
                          fontSize: typography.fontSize.sm,
                          letterSpacing: typography.letterSpacing.wide,
                        }}
                      >
                        {notification.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.textSecondary,
                          display: 'block',
                          mb: 0.5,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                        }}
                      >
                        {notification.message}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.textMuted,
                          fontSize: typography.fontSize.xs,
                          fontFamily: typography.fontFamily.mono,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                        }}
                      >
                        {formatTime(notification.timestamp)}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              );
            })}
          </Box>
        )}
      </Menu>
    </>
  );
};
